/**
 * ASCII Hero Engine - Master Engine Class
 * Self-contained engine for interactive ASCII typography portrait rendering,
 * swappable mid-grey area masking (beard/texture zone), constant kinetic
 * glitching & texting decode stream, and ambient ASCII dust.
 */

import { Easings } from "../utils/easings.js";
import { parseColorToRgb, isPointInPolygon } from "../utils/math.js";
import { AsciiParticle } from "../core/AsciiParticle.js";
import { AsciiDustGlyph } from "../core/AsciiDustGlyph.js";
import { AsciiIllustration } from "../illustrations/AsciiIllustration.js";
import { AsciiBlurRectangle } from "../illustrations/AsciiBlurRectangle.js";
import { AsciiConcentricCircles } from "../illustrations/AsciiConcentricCircles.js";
import { AsciiTimeline } from "../timeline/AsciiTimeline.js";
import {
  DEFAULT_GLITCH_COLORS,
  DEFAULT_MID_GREY_MASK,
  DEFAULT_BASE_RAMP,
  DEFAULT_BASE_GLITCH,
  DEFAULT_DUST_CHARS,
  DEFAULT_MID_GREY_RAMP,
  DEFAULT_MID_GREY_GLITCH,
} from "../constants.js";

export class AsciiHeroEngine {
  constructor(userConfig = {}) {
    this.config = Object.assign(
      {
        // Grid & typography
        charWidth: 20,
        lineHeight: 22,
        fontSize: 128,
        fontFamily: "Doto, DM Mono, SF Mono, Fira Code, monospace",
        fontWeight: "800",

        // Cinematic Spotlight, Easings & Delayed Decay
        spotlightRadius: 280, // Radius of the luminous spotlight beam in px
        spotlightPower: 1.0, // Spotlight intensity multiplier
        spotlightAttackSpeed: 0.16, // Eased spotlight illumination rise speed
        spotlightDecaySpeed: 0.048, // Luxurious slow decay speed for lingering glow wake
        mouseLag: 0.09, // Heavy silk inertia lag factor for mouse interpolation
        parallaxAmount: 6, // 3D perspective depth parallax

        // Background photo opacity
        bgOpacity: 0.5,

        // Constant glitch & texting stream with Radiant Mouse Gradient
        glitchSpeed: 1.0, // Master glitch speed
        glitchRate: 0.04, // Base glitch mutation density
        glitchRadius: 420, // Radius of radiant mouse glitch influence in px
        glitchBaseSpeed: 0.08, // Speed factor far away from mouse (slow calm texting)
        glitchPeakMultiplier: 4.0, // Speed multiplier right at the mouse cursor (fast decode)

        // Dynamic Chromatic Glitch Color Palette
        glitchColors: DEFAULT_GLITCH_COLORS,
        glitchColorsRGB: DEFAULT_GLITCH_COLORS.map(parseColorToRgb),
        glitchColorChance: 0.45, // Probability (0.0 to 1.0) that a glitched char gets a color

        // Ambient ASCII dust (with Stop-Motion kinematics)
        dustCount: 90,
        dustSpeed: 1.0,
        dustChars: DEFAULT_DUST_CHARS,
        dustStopMotion: true, // Enable stop-motion frame quantization
        dustFps: 8, // Stop-motion frame rate
        dustJitter: 1.6, // Micro-step tactile film jitter in px
        dustMouseInteraction: false, // Mouse repulsion on dust
        dustParallax: true, // 3D mouse parallax on dust
        dustMouseRadius: 150,
        dustMousePower: 2.4,

        // Mid-Grey / Accent Zone Configuration (swappable per picture)
        midGreyMask: DEFAULT_MID_GREY_MASK,
        midGreyColor: "#dddddd",
        midGreyColorRGB: [68, 68, 68],
        midGreyRamp: DEFAULT_MID_GREY_RAMP,
        midGreyGlitchChars: DEFAULT_MID_GREY_GLITCH,

        // Base silhouette character sets
        baseRamp: DEFAULT_BASE_RAMP,
        baseGlitchChars: DEFAULT_BASE_GLITCH,
      },
      userConfig
    );

    if (userConfig.glitchColors && !userConfig.glitchColorsRGB) {
      this.config.glitchColorsRGB = userConfig.glitchColors.map(parseColorToRgb);
    }
    if (userConfig.midGreyColor && !userConfig.midGreyColorRGB) {
      this.config.midGreyColorRGB = parseColorToRgb(userConfig.midGreyColor);
    }

    this.canvas = null;
    this.ctx = null;
    this.blurCanvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
    this.blurCtx = this.blurCanvas ? this.blurCanvas.getContext("2d", { willReadFrequently: true }) : null;
    this.width = 0;
    this.height = 0;
    this.dpr = 1;

    this.img = typeof Image !== "undefined" ? new Image() : null;
    this.isLoaded = false;
    this.particles = [];
    this.dust = [];
    this.illustrations = [];
    this.layout = { posX: 0, posY: 0, renderW: 0, renderH: 0 };

    // Crossfading and picture transition state
    this.prevImg = null;
    this.prevParticles = null;
    this.prevLayout = null;
    this.prevMidGreyColorRGB = null;
    this.isTransitioning = false;
    this.transitionProgress = 0;
    this.transitionDuration = 1000;
    this.transitionStartTime = 0;

    // Timeline instance
    this.timeline = null;

    if (userConfig.illustrations) {
      this.setIllustrations(userConfig.illustrations);
    }
    if (userConfig.timeline) {
      this.setTimeline(userConfig.timeline);
    }

    this.mouse = {
      x: -9999,
      y: -9999,
      targetX: -9999,
      targetY: -9999,
      isHovering: false,
    };

    this.currentParallaxX = 0;
    this.currentParallaxY = 0;

    this.animationFrameId = null;
    this.isDestroyed = false;

    // Bind event handlers
    this._onResize = this._onResize.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._animate = this._animate.bind(this);
  }

  /**
   * Mount canvas and start engine
   */
  mount(canvasElement, initialImageSrc = null, midGreyMask = null) {
    if (typeof document === "undefined") return this;

    if (typeof canvasElement === "string") {
      canvasElement = document.querySelector(canvasElement);
    }
    if (!canvasElement) {
      throw new Error("[AsciiHeroEngine] Valid canvas element required.");
    }

    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext("2d", { alpha: false, willReadFrequently: true });

    if (midGreyMask) {
      this.config.midGreyMask = midGreyMask;
    }

    this._bindEvents();
    this._onResize();

    if (initialImageSrc) {
      this.loadImage(initialImageSrc, midGreyMask);
    }

    this._animate();
    return this;
  }

  /**
   * Attach or initialize a timeline
   */
  setTimeline(timelineConfigOrInstance) {
    if (timelineConfigOrInstance instanceof AsciiTimeline) {
      this.timeline = timelineConfigOrInstance;
      this.timeline.attach(this);
    } else if (typeof timelineConfigOrInstance === "object") {
      this.timeline = new AsciiTimeline(Object.assign({ engine: this }, timelineConfigOrInstance));
      this.timeline.attach(this);
    }
    return this.timeline;
  }

  playTimeline() {
    if (this.timeline) this.timeline.play();
  }
  pauseTimeline() {
    if (this.timeline) this.timeline.pause();
  }
  toggleTimeline() {
    if (this.timeline) this.timeline.togglePlay();
  }
  nextScene() {
    if (this.timeline) this.timeline.next();
  }
  prevScene() {
    if (this.timeline) this.timeline.prev();
  }
  goToScene(idOrIndex) {
    if (this.timeline) this.timeline.goToScene(idOrIndex);
  }

  /**
   * Internal helper to normalize illustration instances
   */
  _normalizeIllustrations(illustrationsArray) {
    if (!Array.isArray(illustrationsArray)) return [];
    return illustrationsArray.map((item) => {
      if (
        item instanceof AsciiIllustration ||
        item instanceof AsciiBlurRectangle ||
        item instanceof AsciiConcentricCircles
      ) {
        return item;
      }
      if (item && item.type === "blur-rectangle") return new AsciiBlurRectangle(item);
      if (item && (item.type === "concentric-circles" || item.type === "circles" || item.type === "ripple-rings")) {
        return new AsciiConcentricCircles(item);
      }
      return new AsciiIllustration(item);
    });
  }

  /**
   * Set or swap the illustrations array
   */
  setIllustrations(illustrationsArray) {
    this.illustrations = this._normalizeIllustrations(illustrationsArray);
  }

  /**
   * Smoothly crossfade illustrations without reloading image
   */
  swapIllustrations(newIllustrations, transitionDuration = 1200) {
    if (!this.isLoaded || transitionDuration <= 0) {
      this.setIllustrations(newIllustrations);
      return;
    }

    this.prevIllustrations = this.illustrations ? [...this.illustrations] : [];
    this.pendingIllustrations = this._normalizeIllustrations(newIllustrations);
    this.isTransitioning = true;
    this.hasSwappedMidpoint = false;
    this.transitionStartTime = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.transitionDuration = transitionDuration;
  }

  /**
   * Get an illustration by name
   */
  getIllustration(name) {
    return this.illustrations.find((item) => item.name === name) || null;
  }

  /**
   * Add a single illustration with optional smooth fade-in
   */
  addIllustration(illustration, fadeDuration = 0) {
    let item = illustration;
    if (
      !(
        item instanceof AsciiIllustration ||
        item instanceof AsciiBlurRectangle ||
        item instanceof AsciiConcentricCircles
      )
    ) {
      if (item && item.type === "blur-rectangle") item = new AsciiBlurRectangle(item);
      else if (item && (item.type === "concentric-circles" || item.type === "circles" || item.type === "ripple-rings"))
        item = new AsciiConcentricCircles(item);
      else item = new AsciiIllustration(item);
    }

    if (fadeDuration > 0) {
      const targetOp = item.opacity !== undefined ? item.opacity : 1.0;
      item.opacity = 0;
      item.targetOpacity = targetOp;
      item.fadeSpeed = Math.max(0.015, 1000 / (fadeDuration * 60));
    }

    this.illustrations.push(item);
    return item;
  }

  /**
   * Remove an illustration with optional smooth fade-out
   */
  removeIllustration(nameOrInstance, fadeDuration = 0) {
    const item = typeof nameOrInstance === "string" ? this.getIllustration(nameOrInstance) : nameOrInstance;
    if (!item) return;

    if (fadeDuration > 0) {
      item.targetOpacity = 0;
      item.fadeSpeed = Math.max(0.015, 1000 / (fadeDuration * 60));
      item._toRemove = true;
    } else {
      this.illustrations = this.illustrations.filter((el) => el !== item);
    }
  }

  /**
   * Clear all illustrations with optional fade-out
   */
  clearIllustrations(fadeDuration = 0) {
    if (fadeDuration > 0) {
      for (let i = 0; i < this.illustrations.length; i++) {
        this.removeIllustration(this.illustrations[i], fadeDuration);
      }
    } else {
      this.illustrations = [];
    }
  }

  /**
   * Internal helper to sample image data into particles
   */
  _sampleParticles(image, layout, config) {
    if (!image || !layout.renderW || !layout.renderH || typeof document === "undefined") return [];

    const offCanvas = document.createElement("canvas");
    const offCtx = offCanvas.getContext("2d", { willReadFrequently: true });
    offCanvas.width = this.width;
    offCanvas.height = this.height;

    try {
      offCtx.drawImage(image, layout.posX, layout.posY, layout.renderW, layout.renderH);
      const imgData = offCtx.getImageData(0, 0, this.width, this.height).data;

      const charW = config.charWidth;
      const lineH = config.lineHeight;
      const cols = Math.floor(this.width / charW);
      const rows = Math.floor(this.height / lineH);

      const gridOffX = (this.width - cols * charW) * 0.5;
      const gridOffY = (this.height - rows * lineH) * 0.5;

      const mask = config.midGreyMask;
      const hasMask = mask && mask.length >= 3;
      const particles = [];

      for (let r = 0; r < rows; r++) {
        const y = Math.floor(gridOffY + r * lineH);
        for (let c = 0; c < cols; c++) {
          const x = Math.floor(gridOffX + c * charW);
          const pIdx = (y * this.width + x) * 4;

          const red = imgData[pIdx];
          const green = imgData[pIdx + 1];
          const blue = imgData[pIdx + 2];
          const brightness = 0.299 * red + 0.587 * green + 0.114 * blue;

          if (brightness > 22) {
            const u = (x - layout.posX) / layout.renderW;
            const v = (y - layout.posY) / layout.renderH;
            const inPolygon = hasMask && u >= 0 && u <= 1 && v >= 0 && v <= 1 ? isPointInPolygon(u, v, mask) : false;

            const ramp = inPolygon ? config.midGreyRamp : config.baseRamp;
            const charIdx = Math.min(ramp.length - 1, Math.max(0, Math.floor((brightness / 255) * ramp.length)));
            const char = ramp[charIdx];

            particles.push(new AsciiParticle(x, y, brightness, char, inPolygon));
          }
        }
      }
      return particles;
    } catch (e) {
      console.error("[AsciiHeroEngine] Sampling failed:", e);
      return [];
    }
  }

  /**
   * Load image directly
   */
  loadImage(imageSrc, newMidGreyMask = null, newIllustrations = null) {
    this.isLoaded = false;
    if (newMidGreyMask) {
      this.config.midGreyMask = newMidGreyMask;
    }
    if (newIllustrations !== null) {
      this.setIllustrations(newIllustrations);
    }

    if (typeof Image === "undefined") return;
    this.img = new Image();
    if (typeof imageSrc === "string" && !imageSrc.startsWith("data:")) {
      this.img.crossOrigin = "anonymous";
    }

    const onImageReady = () => {
      this.isLoaded = true;
      this._onResize();
    };

    this.img.onload = onImageReady;
    this.img.onerror = (err) => {
      console.error("[AsciiHeroEngine] Failed to load image:", err);
    };

    this.img.src = imageSrc;

    if (this.img.complete && this.img.naturalWidth > 0) {
      onImageReady();
    }
  }

  /**
   * Smoothly swap portrait image with total fadeout to black then fade in
   */
  swapImage(imageSrc, newMidGreyMask = null, newIllustrations = null, transitionDuration = 1200) {
    if (!this.isLoaded || transitionDuration <= 0) {
      this.loadImage(imageSrc, newMidGreyMask, newIllustrations);
      return;
    }

    // Preserve previous state for total fadeout
    this.prevImg = this.img;
    this.prevParticles = this.particles;
    this.prevLayout = Object.assign({}, this.layout);
    this.prevIllustrations = this.illustrations ? [...this.illustrations] : [];
    this.prevMidGreyColorRGB = this.config.midGreyColorRGB ? [...this.config.midGreyColorRGB] : [68, 68, 68];

    if (newMidGreyMask) {
      this.config.midGreyMask = newMidGreyMask;
    }

    // Store next illustrations to mount at the midpoint of transition
    if (newIllustrations !== null) {
      this.pendingIllustrations = this._normalizeIllustrations(newIllustrations);
    } else {
      this.pendingIllustrations = null;
    }

    if (typeof Image === "undefined") return;
    const nextImg = new Image();
    if (typeof imageSrc === "string" && !imageSrc.startsWith("data:")) {
      nextImg.crossOrigin = "anonymous";
    }

    nextImg.onload = () => {
      const imgAspect =
        nextImg.naturalWidth && nextImg.naturalHeight ? nextImg.naturalWidth / nextImg.naturalHeight : 16 / 9;

      this.layout = {
        renderH: this.height,
        renderW: this.height * imgAspect,
        posX: this.width - this.height * imgAspect,
        posY: 0,
      };

      this.img = nextImg;
      this.particles = this._sampleParticles(this.img, this.layout, this.config);
      this.isLoaded = true;
    };

    nextImg.src = imageSrc;
    if (nextImg.complete && nextImg.naturalWidth > 0) {
      nextImg.onload();
    }

    this.isTransitioning = true;
    this.hasSwappedMidpoint = false;
    this.transitionStartTime = typeof performance !== "undefined" ? performance.now() : Date.now();
    this.transitionDuration = transitionDuration;
  }

  /**
   * Apply a timeline scene definition
   */
  applyScene(scene, transitionDuration = 1000) {
    if (!scene) return;

    // 1. Config overrides
    if (scene.config) {
      if (scene.config.midGreyColor) {
        this.config.midGreyColor = scene.config.midGreyColor;
        this.config.midGreyColorRGB = parseColorToRgb(scene.config.midGreyColor);
      }
      if (scene.config.dustCount !== undefined && scene.config.dustCount !== this.config.dustCount) {
        this.config.dustCount = scene.config.dustCount;
        this.setDustCount(scene.config.dustCount);
      }
      if (scene.config.spotlightRadius !== undefined) this.config.spotlightRadius = scene.config.spotlightRadius;
      if (scene.config.glitchSpeed !== undefined) this.config.glitchSpeed = scene.config.glitchSpeed;
      if (scene.config.bgOpacity !== undefined) this.config.bgOpacity = scene.config.bgOpacity;
    }

    // 2. Mid-grey polygon mask
    const mask = scene.midGreyMask || null;

    // 3. Normalize illustrations for the scene
    const targetIllustrations = scene.illustrations !== undefined ? scene.illustrations : [];

    // 4. Swap or transition background picture and illustrations
    if (scene.image) {
      this.swapImage(scene.image, mask, targetIllustrations, transitionDuration);
    } else {
      if (transitionDuration > 0 && this.isLoaded) {
        this.swapIllustrations(targetIllustrations, transitionDuration);
      } else {
        this.setIllustrations(targetIllustrations);
      }
    }

    // 5. Custom onEnter hook
    if (typeof scene.onEnter === "function") {
      scene.onEnter(scene, this);
    }
  }

  /**
   * Adjust dust count gracefully
   */
  setDustCount(count) {
    if (count > this.dust.length) {
      const diff = count - this.dust.length;
      for (let i = 0; i < diff; i++) {
        this.dust.push(new AsciiDustGlyph(this.width, this.height, false, this.config));
      }
    } else if (count < this.dust.length) {
      this.dust.splice(count);
    }
  }

  /**
   * Update mid-grey mask polygon on the fly
   */
  setMidGreyMask(polygon) {
    this.config.midGreyMask = polygon;
    this.sampleImage();
  }

  /**
   * Update configuration values
   */
  setConfig(newConfig) {
    if (newConfig.glitchColors && !newConfig.glitchColorsRGB) {
      newConfig.glitchColorsRGB = newConfig.glitchColors.map(parseColorToRgb);
    }
    if (newConfig.midGreyColor && !newConfig.midGreyColorRGB) {
      newConfig.midGreyColorRGB = parseColorToRgb(newConfig.midGreyColor);
    }
    Object.assign(this.config, newConfig);
    if (newConfig.charWidth || newConfig.lineHeight || newConfig.midGreyMask || newConfig.dustCount !== undefined) {
      this.sampleImage();
    }
  }

  /**
   * Sample image pixels into text grid particles
   */
  sampleImage() {
    if (!this.width || !this.height || typeof document === "undefined") return;

    // Initialize ambient dust
    this.dust = [];
    const dustCount = this.config.dustCount !== undefined ? this.config.dustCount : 90;
    for (let i = 0; i < dustCount; i++) {
      this.dust.push(new AsciiDustGlyph(this.width, this.height, true, this.config));
    }

    if (!this.isLoaded || !this.img) return;

    this.particles = [];

    const offCanvas = document.createElement("canvas");
    const offCtx = offCanvas.getContext("2d", { willReadFrequently: true });

    offCanvas.width = this.width;
    offCanvas.height = this.height;

    const imgAspect =
      this.img.naturalWidth && this.img.naturalHeight ? this.img.naturalWidth / this.img.naturalHeight : 16 / 9;

    this.layout.renderH = this.height;
    this.layout.renderW = this.layout.renderH * imgAspect;
    this.layout.posX = this.width - this.layout.renderW;
    this.layout.posY = 0;

    try {
      offCtx.drawImage(this.img, this.layout.posX, this.layout.posY, this.layout.renderW, this.layout.renderH);
      const imgData = offCtx.getImageData(0, 0, this.width, this.height).data;

      const charW = this.config.charWidth;
      const lineH = this.config.lineHeight;

      const numCols = Math.ceil(this.width / charW);
      const numRows = Math.ceil(this.height / lineH);
      const mask = this.config.midGreyMask;

      for (let r = 0; r < numRows; r++) {
        const y = r * lineH;

        for (let c = 0; c < numCols; c++) {
          const x = c * charW;

          const sampleX = Math.min(this.width - 1, Math.floor(x + charW / 2));
          const sampleY = Math.min(this.height - 1, Math.floor(y + lineH / 2));

          const idx = (sampleY * this.width + sampleX) * 4;
          const red = imgData[idx];
          const green = imgData[idx + 1];
          const blue = imgData[idx + 2];
          const a = imgData[idx + 3];

          const lum = 0.299 * red + 0.587 * green + 0.114 * blue;

          // Check if cell is within the swappable Mid-Grey Area
          const normU = (sampleX - this.layout.posX) / this.layout.renderW;
          const normV = (sampleY - this.layout.posY) / this.layout.renderH;
          const inMidGrey =
            normU >= 0 && normU <= 1 && normV >= 0 && normV <= 1 && isPointInPolygon(normU, normV, mask);

          if (inMidGrey) {
            const midGreyLum = Math.max(lum, 35 + Math.sin(r * 2.8 + c * 3.4) * 22);
            const rampIdx = Math.min(
              this.config.midGreyRamp.length - 1,
              Math.max(0, Math.floor((midGreyLum / 255) * this.config.midGreyRamp.length))
            );
            const char = this.config.midGreyRamp[rampIdx];
            this.particles.push(new AsciiParticle(x, y, midGreyLum, char, true));
          } else if (a > 40 && lum > 14) {
            const rampIdx = Math.min(
              this.config.baseRamp.length - 1,
              Math.max(1, Math.floor((lum / 255) * this.config.baseRamp.length))
            );
            const char = this.config.baseRamp[rampIdx];
            this.particles.push(new AsciiParticle(x, y, lum, char, false));
          }
        }
      }
    } catch (err) {
      console.warn("[AsciiHeroEngine] Canvas pixel extraction notice:", err);
    }
  }

  _onResize() {
    if (typeof window === "undefined") return;

    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    if (!this.canvas) return;

    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;
    this.canvas.style.width = this.width + "px";
    this.canvas.style.height = this.height + "px";

    if (this.blurCanvas) {
      this.blurCanvas.width = this.canvas.width;
      this.blurCanvas.height = this.canvas.height;
    }

    if (this.ctx) {
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    this.sampleImage();
  }

  _onMouseMove(e) {
    this.mouse.targetX = e.clientX;
    this.mouse.targetY = e.clientY;
    this.mouse.isHovering = true;
  }

  _onMouseLeave() {
    this.mouse.targetX = -9999;
    this.mouse.targetY = -9999;
    this.mouse.isHovering = false;
  }

  _onTouchMove(e) {
    if (e.touches && e.touches.length > 0) {
      this.mouse.targetX = e.touches[0].clientX;
      this.mouse.targetY = e.touches[0].clientY;
      this.mouse.isHovering = true;
    }
  }

  _onTouchEnd() {
    this.mouse.targetX = -9999;
    this.mouse.targetY = -9999;
    this.mouse.isHovering = false;
  }

  _bindEvents() {
    if (typeof window === "undefined") return;
    window.addEventListener("resize", this._onResize);
    window.addEventListener("mousemove", this._onMouseMove);
    window.addEventListener("mouseleave", this._onMouseLeave);
    window.addEventListener("touchmove", this._onTouchMove, { passive: true });
    window.addEventListener("touchend", this._onTouchEnd);
  }

  _unbindEvents() {
    if (typeof window === "undefined") return;
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("mousemove", this._onMouseMove);
    window.removeEventListener("mouseleave", this._onMouseLeave);
    window.removeEventListener("touchmove", this._onTouchMove);
    window.removeEventListener("touchend", this._onTouchEnd);
  }

  _animate() {
    if (this.isDestroyed || typeof window === "undefined") return;
    this.animationFrameId = requestAnimationFrame(this._animate);

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();

    // Update timeline playback
    if (this.timeline) {
      this.timeline.update(now);
    }

    // Prune removed illustrations that have completed fading out
    if (this.illustrations && this.illustrations.length > 0) {
      this.illustrations = this.illustrations.filter((el) => !(el._toRemove && el.opacity <= 0.005));
    }

    // Ultra-smooth mouse lag
    if (this.mouse.targetX !== -9999) {
      const mouseEase = this.config.mouseLag || 0.09;
      this.mouse.x += (this.mouse.targetX - this.mouse.x) * mouseEase;
      this.mouse.y += (this.mouse.targetY - this.mouse.y) * mouseEase;
    } else {
      this.mouse.x = -9999;
      this.mouse.y = -9999;
    }

    // Eased 3D Parallax
    const rawPx =
      ((this.mouse.targetX !== -9999 ? this.mouse.x : this.width * 0.5) - this.width * 0.5) / (this.width * 0.5);
    const rawPy =
      ((this.mouse.targetY !== -9999 ? this.mouse.y : this.height * 0.5) - this.height * 0.5) / (this.height * 0.5);

    const signX = Math.sign(rawPx) || 1;
    const signY = Math.sign(rawPy) || 1;
    const easedPx = signX * Easings.easeInOutSine(Math.min(1, Math.abs(rawPx))) * (this.config.parallaxAmount || 6);
    const easedPy = signY * Easings.easeInOutSine(Math.min(1, Math.abs(rawPy))) * (this.config.parallaxAmount || 6);

    this.currentParallaxX += (easedPx - this.currentParallaxX) * 0.07;
    this.currentParallaxY += (easedPy - this.currentParallaxY) * 0.07;

    const parallaxX = this.currentParallaxX;
    const parallaxY = this.currentParallaxY;

    // Sequential Transition
    let prevAlpha = 0.0;
    let currAlpha = 1.0;
    let activeIllustrations = this.illustrations;
    let illustrationAlpha = 1.0;
    let activeLayout = this.layout;

    if (this.isTransitioning) {
      const elapsed = now - this.transitionStartTime;
      const rawProgress = Math.min(1.0, elapsed / this.transitionDuration);

      if (rawProgress < 0.5) {
        const pOut = rawProgress / 0.5;
        prevAlpha = Math.max(0, 1.0 - Easings.easeInOutCubic(pOut));
        currAlpha = 0.0;
        activeIllustrations = this.prevIllustrations || this.illustrations;
        illustrationAlpha = prevAlpha;
        activeLayout = this.prevLayout || this.layout;
      } else {
        if (!this.hasSwappedMidpoint) {
          this.hasSwappedMidpoint = true;
          if (this.pendingIllustrations !== null) {
            this.illustrations = this.pendingIllustrations;
            this.pendingIllustrations = null;
          }
        }
        const pIn = (rawProgress - 0.5) / 0.5;
        prevAlpha = 0.0;
        currAlpha = Math.min(1.0, Easings.easeInOutCubic(pIn));
        activeIllustrations = this.illustrations;
        illustrationAlpha = currAlpha;
        activeLayout = this.layout;
      }

      this.transitionProgress = rawProgress;

      if (rawProgress >= 1.0) {
        this.isTransitioning = false;
        this.hasSwappedMidpoint = false;
        this.prevImg = null;
        this.prevParticles = null;
        this.prevLayout = null;
        this.prevIllustrations = null;
        this.prevMidGreyColorRGB = null;
      }
    }

    if (!this.ctx) return;

    // Clear canvas
    this.ctx.fillStyle = "#000000";
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Draw the background original photo
    if (this.config.bgOpacity > 0) {
      if (prevAlpha > 0.001 && this.prevImg && this.prevLayout) {
        const bgPrevX = this.prevLayout.posX + parallaxX * 0.25;
        const bgPrevY = this.prevLayout.posY + parallaxY * 0.25;
        this.ctx.save();
        this.ctx.globalAlpha = this.config.bgOpacity * prevAlpha;
        this.ctx.drawImage(this.prevImg, bgPrevX, bgPrevY, this.prevLayout.renderW, this.prevLayout.renderH);
        this.ctx.restore();
      }

      if (currAlpha > 0.001 && this.isLoaded && this.layout.renderW > 0) {
        const bgX = this.layout.posX + parallaxX * 0.25;
        const bgY = this.layout.posY + parallaxY * 0.25;
        this.ctx.save();
        this.ctx.globalAlpha = this.config.bgOpacity * currAlpha;
        this.ctx.drawImage(this.img, bgX, bgY, this.layout.renderW, this.layout.renderH);
        this.ctx.restore();
      }
    }

    // Setup font rendering
    this.ctx.font = `${this.config.fontWeight} ${this.config.fontSize}px ${this.config.fontFamily}`;
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "top";

    // Update and draw ambient ASCII dust
    if (this.dust.length > 0) {
      const dustFontSize = this.config.dustFontSize || Math.max(12, Math.round(this.config.fontSize * 0.9));
      this.ctx.font = `${this.config.fontWeight} ${dustFontSize}px ${this.config.fontFamily}`;
      for (let i = 0; i < this.dust.length; i++) {
        this.dust[i].update(this.mouse, this.width, this.height, this.config, parallaxX, parallaxY);
        this.dust[i].draw(this.ctx, this.config, parallaxX, parallaxY);
      }
    }

    // Snapshot for background blur
    const hasBlurElements =
      activeIllustrations &&
      activeIllustrations.some((el) => el instanceof AsciiBlurRectangle || (el && el.blur > 0));

    // Update and draw background-layer illustrations
    if (activeIllustrations && activeIllustrations.length > 0 && illustrationAlpha > 0.001) {
      if (hasBlurElements && this.blurCtx && this.blurCanvas) {
        this.blurCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.blurCtx.drawImage(this.canvas, 0, 0);
      }
      this.ctx.save();
      this.ctx.globalAlpha = illustrationAlpha;
      for (let i = 0; i < activeIllustrations.length; i++) {
        if (activeIllustrations[i].layer === "background") {
          activeIllustrations[i].update(this.mouse, activeLayout, this.config, parallaxX, parallaxY);
          activeIllustrations[i].draw(this.ctx, this.config, parallaxX, parallaxY, activeLayout, this.blurCanvas);
        }
      }
      this.ctx.restore();
    }

    // Reset font for portrait particles
    this.ctx.font = `${this.config.fontWeight} ${this.config.fontSize}px ${this.config.fontFamily}`;

    // Draw previous particles during fadeout
    if (prevAlpha > 0.001 && this.prevParticles && this.prevParticles.length > 0) {
      const prevConfig = this.prevMidGreyColorRGB
        ? Object.assign({}, this.config, { midGreyColorRGB: this.prevMidGreyColorRGB })
        : this.config;
      for (let i = 0; i < this.prevParticles.length; i++) {
        const p = this.prevParticles[i];
        p.update(this.mouse, prevConfig, parallaxX, parallaxY);
        p.draw(this.ctx, prevConfig, prevAlpha);
      }
    }

    // Draw current particles during fadein
    if (currAlpha > 0.001 && this.particles && this.particles.length > 0) {
      for (let i = 0; i < this.particles.length; i++) {
        const p = this.particles[i];
        p.update(this.mouse, this.config, parallaxX, parallaxY);
        p.draw(this.ctx, this.config, currAlpha);
      }
    }

    // Snapshot again for foreground blur
    if (activeIllustrations && activeIllustrations.length > 0 && illustrationAlpha > 0.001) {
      if (hasBlurElements && this.blurCtx && this.blurCanvas) {
        this.blurCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.blurCtx.drawImage(this.canvas, 0, 0);
      }
      this.ctx.save();
      this.ctx.globalAlpha = illustrationAlpha;
      for (let i = 0; i < activeIllustrations.length; i++) {
        if (activeIllustrations[i].layer !== "background") {
          activeIllustrations[i].update(this.mouse, activeLayout, this.config, parallaxX, parallaxY);
          activeIllustrations[i].draw(this.ctx, this.config, parallaxX, parallaxY, activeLayout, this.blurCanvas);
        }
      }
      this.ctx.restore();
    }
  }

  destroy() {
    this.isDestroyed = true;
    if (this.animationFrameId && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.animationFrameId);
    }
    this._unbindEvents();
    this.particles = [];
    this.dust = [];
  }
}
